'use client';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';
import { getSmartListingProducts } from '@util/firestore/listing/listing.service';
import { getProductByMpn, getProductId } from '@util/firestore/products';
import { trackUserSearch } from '@util/firestore/recommendations';
import { saveSearchQuery } from '@util/firestore/search';
import useDebounce from '@util/hooks/useDebounce';
import { useAuth } from 'context/AuthContext';
import { ProductDocument } from 'models/product';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { SearchResponseHit } from 'typesense/lib/Typesense/Documents';
import { useCreateListing } from '../_context';
import { FirebaseCallable, httpsCallable, functions } from '@util/firebase';
import { Bike, RideType } from 'models/shared';
import { formatCurrency } from '@util/index';
import { ScanBarcodeIcon } from 'lucide-react';
import { getCategoryByID } from '@util/firestore/categories';
import { slugify } from '@util/urlHelpers';
import { CategoryDocument } from 'models/category';
import { logError } from '@util/logError';
import { ProductVariationImages } from '@c/cards/MXProductCard';

type FitmentBike = { year: number; make: string; model: string };

export default function SelectListingTypeStep() {
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const { setCurrentStep, steps, form, setCategoryInfo } = useCreateListing();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProduct, setSelectedProduct] =
    useState<ProductDocument | null>(null);
  const { userDoc } = useAuth();
  const [partNumber, setPartNumber] = useState('');
  const [partResult, setPartResult] = useState<null | Partial<ProductDocument>>(
    null
  );
  const [partLoading, setPartLoading] = useState(false);
  const [partError, setPartError] = useState<string | null>(null);
  // const [dataDepotImage, setDataDepotImage] = useState<string | null>(null);
  const [fitmentData, setFitmentData] = useState<{
    bikes: FitmentBike[];
    rideTypes: RideType[];
  } | null>(null);
  const handleSearchChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = ev.target.value;
    setSearchTerm(newSearchTerm);
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data, isLoading } = useQuery({
    queryKey: ['smartListingProducts', debouncedSearchTerm],
    queryFn: () => getSmartListingProducts(debouncedSearchTerm),
    enabled: !!debouncedSearchTerm,
    // throwOnError: true,
  });

  const handleSearchKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      const keyword = (ev.target as HTMLInputElement).value;
      if (keyword) {
        if (userDoc) {
          saveSearchQuery({
            query: keyword,
            uid: userDoc.uid,
          });
        }
        trackUserSearch({
          term: keyword,
          product_clicked: null,
        });
      }
    }
  };

  const handleSetSelectedProduct = (product: ProductDocument) => {
    setSelectedProduct(product);
  };

  const handleContinueClick = async () => {
    if (selectedProduct) {
      form.reset({
        sources: [
          {
            id: selectedProduct.id,
            collection:
              selectedProduct.slug.includes('https') ||
              (selectedProduct as any).data_source
                ? 'data_depot'
                : 'products',
          },
        ],
        id: getProductId(),
        brand: selectedProduct.brand,
        is_flat_rate: true,
        shipping_costs: [
          {
            code: 'US',
            cost: 0,
            name: 'Continental US',
          },
        ],
        size: selectedProduct.size,
        date_updated: Date.now(),
        date_added: Date.now(),
        created: Date.now(),
        gender: selectedProduct.gender,
        title: selectedProduct.title,
        description: selectedProduct.description,
        // sku: selectedProduct.sku,
        mpn: selectedProduct.mpn,
        ride_types: selectedProduct.ride_types ?? [],
        images: [],
        category: selectedProduct.category,
        categories: selectedProduct.categories,
        category1: selectedProduct.category1,
        category2: selectedProduct.category2,
        category3: selectedProduct.category3,
        condition: selectedProduct.condition,
        stock: 1,
        slug: '',
        country_code: selectedProduct.country_code ?? 'US',
        og_price: 1,
        price: 1,
        on_sale: false,
        seller_id: userDoc?.uid,
        account_id: userDoc?.account_id,
        fitting_bikes: selectedProduct.fitting_bikes,
        from_web: true,
        seller_type: userDoc?.seller_type ?? 'individual',
        out_of_stock: false,
        thumbnail: '',
      });
      setCurrentStep(steps.findIndex((s) => s.id === 'create-listing'));
    } else if (partResult) {
      const newForm = {
        id: getProductId(),
        brand: partResult.brand ?? '',
        is_flat_rate: true,
        shipping_costs: [
          {
            code: 'US',
            cost: 0,
            name: 'Continental US',
            enabled: true,
          },
        ],
        date_updated: Date.now(),
        date_added: Date.now(),
        description:
          partResult.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12' // only use description from mxlocker direct
            ? partResult.description ?? ''
            : '',
        created: Date.now(),
        title: partResult.title ?? '',
        sku: '',
        mpn: [partNumber],
        ride_types: partResult.ride_types ?? fitmentData?.rideTypes ?? [],
        images: [],
        category: partResult.category ?? 'Bike Parts',
        categories: partResult.categories ?? ['Bike Parts'],
        category1: partResult.category1 ?? null,
        category2: partResult.category2 ?? null,
        category3: partResult.category3 ?? null,
        condition: 'Used',
        stock: 1,
        slug: '',
        country_code: 'US',
        og_price: partResult.og_price ?? partResult.price ?? 0,
        price: partResult.price ?? 0,
        on_sale: false,
        seller_id: userDoc?.uid ?? '',
        account_id: userDoc?.account_id ?? '',
        fitting_bikes: partResult.fitting_bikes
          ? partResult.fitting_bikes
          : fitmentData?.bikes.length
          ? {
              bikes: fitmentData.bikes as Bike[],
              universal: false,
            }
          : undefined,
        from_web: true,
        seller_type: userDoc?.seller_type ?? 'individual',
        out_of_stock: false,
        thumbnail: '',
      } as Partial<ProductDocument>;
      form.reset(newForm);
      if (newForm.category1) {
        setCurrentStep(steps.findIndex((s) => s.id === 'create-listing'));
        return;
      } else if (newForm.ride_types?.length) {
        try {
          const rideType = newForm.ride_types[0];
          const sport = await getCategoryByID(
            `categories_new/${slugify(rideType.toLowerCase())}`
          );
          const partCategory =
            rideType === 'Street'
              ? `parts`
              : `${slugify(rideType.toLowerCase().replace(/s$/, ''))}-parts`;
          const category = await getCategoryByID(
            `${sport.id}/category1_new/${partCategory}`
          );

          if (sport && category) {
            setCategoryInfo([sport, category] as CategoryDocument[]);
            setCurrentStep(
              steps.findIndex((s) => s.id === 'select-listing-category')
            );
          } else {
            setCurrentStep(
              steps.findIndex((s) => s.id === 'select-listing-sport')
            );
          }
        } catch (e) {
          logError(
            e,
            undefined,
            undefined,
            'unable to set category info from list by part number'
          );
          setCurrentStep(
            steps.findIndex((s) => s.id === 'select-listing-sport')
          );
        }
      } else {
        setCurrentStep(steps.findIndex((s) => s.id === 'select-listing-sport'));
      }
    } else {
      setCurrentStep(steps.findIndex((s) => s.id === 'select-listing-sport'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePartNumberSearch = async () => {
    setPartLoading(true);
    setPartError(null);
    setFitmentData(null);
    setPartResult(null);
    try {
      const getListing = httpsCallable<
        { partNumber: string },
        {
          title: string;
          description: string;
          retail_price: number;
          fitment_info: {
            bikes: FitmentBike[];
            ride_types: RideType[];
          };
          dataDepot: ProductDocument | null;
          verifiedSellers: ProductDocument[];
        } | null
      >(functions, FirebaseCallable.listingToolGetListing);
      const fetchMotosport = httpsCallable<
        { partNumber: string },
        { bikes: FitmentBike[]; rideTypes: RideType[] }
      >(functions, FirebaseCallable.listingToolFetchMotosport);

      const getFitmentFromSources = httpsCallable<
        { sku: string },
        {
          bikes: FitmentBike[];
          rideTypes: RideType[];
          sources: { id: string; collection: string }[];
        }
      >(functions, FirebaseCallable.listingToolGetFitmentFromSources);
      const [res, fitmentRes, dataDepotRes, fitmentFromSourcesRes] =
        await Promise.all([
          getListing({ partNumber }),
          fetchMotosport({ partNumber }),
          getProductByMpn([partNumber]),
          getFitmentFromSources({ sku: partNumber }),
        ]);
      if (res.data) {
        if (res.data.dataDepot) {
          setPartResult(res.data.dataDepot);
        } else if (res.data.verifiedSellers.length > 0) {
          setPartResult(res.data.verifiedSellers[0]);
        } else {
          setPartResult(res.data);
        }
      } else {
        setPartError('No part found.');
      }
      if (fitmentFromSourcesRes.data.bikes.length > 0) {
        setFitmentData({
          bikes: fitmentFromSourcesRes.data.bikes,
          rideTypes: fitmentFromSourcesRes.data.rideTypes,
        });
      } else if (fitmentRes.data.bikes.length > 0) {
        setFitmentData(fitmentRes.data);
      } else if (!!dataDepotRes?.fitting_bikes?.bikes.length) {
        setFitmentData({
          bikes: (dataDepotRes?.fitting_bikes?.bikes ?? []) as FitmentBike[],
          rideTypes: dataDepotRes?.ride_types ?? [],
        });
      } else if (res.data?.fitment_info?.bikes) {
        setFitmentData({
          bikes: res.data.fitment_info.bikes,
          rideTypes: res.data.fitment_info.ride_types,
        });
      } else {
        setPartError('No fitment data found.');
      }

      // if (dataDepotRes) {
      // setDataDepotImage(dataDepotRes.thumbnail);
      // }
    } catch (err: any) {
      setPartError(err?.message || 'Failed to fetch part info.');
    } finally {
      setPartLoading(false);
    }
  };

  useEffect(() => {
    const url = window.location.href.split('/');

    if (url.includes('edit-listing')) {
      setCurrentStep(3);
    }
  }, []);

  return (
    <div className="flex h-auto flex-col justify-between gap-y-16 px-8 py-16 sm:mx-auto sm:h-[100rem] sm:max-w-[90rem] sm:px-0 sm:py-32">
      <div className="relative">
        <h1 className="text-[3rem] font-semibold text-black">
          How you want to list it?
        </h1>

        <div className="my-16 flex w-full flex-col justify-between gap-x-8 gap-y-16 sm:flex-row">
          <ListingTypeComponent
            title="Smart Listing"
            // isBeta
            description="A new faster way to list your item."
            icon={<StarsIcon />}
            selectedType={selectedType}
            type={1}
            setSelectedType={() => {
              setPartNumber('');
              setPartResult(null);
              // setDataDepotImage(null);
              setFitmentData(null);
              setSelectedProduct(null);
              setSelectedType(1);
            }}
          />

          <ListingTypeComponent
            title="Start from scratch"
            description="List your item from blank space."
            icon={<PaperIcon variant="red" />}
            selectedType={selectedType}
            type={2}
            setSelectedType={() => {
              setPartNumber('');
              setPartResult(null);
              // setDataDepotImage(null);
              setFitmentData(null);
              setSelectedProduct(null);
              setSelectedType(2);
            }}
          />
        </div>

        {(userDoc?.uid === 'cCfXKurBqrfFOcEYQIrYkVefvBp1' ||
          userDoc?.uid === 'FjBH0nwiHgGV4Y0EXDQS' ||
          userDoc?.roles?.support) && (
          <ListingTypeComponent
            isBeta
            title="List by part number"
            description="List your item from a part number."
            icon={<ScanBarcodeIcon className="h-8 w-8 text-brand-secondary" />}
            selectedType={selectedType}
            type={3}
            setSelectedType={() => {
              setPartNumber('');
              setPartResult(null);
              // setDataDepotImage(null);
              setFitmentData(null);
              setSelectedProduct(null);
              setSelectedType(3);
            }}
          ></ListingTypeComponent>
        )}
        {/* List By Part Number Box */}

        {selectedType === 1 && !selectedProduct && (
          <div className="mt-16 w-full">
            <h3 className="text-[1.8rem] font-medium text-zinc-700">
              Find and match your item
            </h3>
            <div className="mt-4 text-[1.4rem] text-zinc-700">
              Be specific in your search to find the perfect match—try 'KTM 125
              OEM front wheel' instead of just 'front wheel.'
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between rounded-[1.5rem] bg-[#F1F1F1] p-4 text-black">
                <input
                  type="text"
                  className="w-full border-none bg-inherit text-[1.8rem] outline-none"
                  placeholder="Type anything..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                />

                <MagnifyingGlassIcon className="mr-4 h-8 w-8" />
              </div>
            </div>
            <ProductHits
              isLoading={isLoading}
              hits={data}
              searchTerm={searchTerm ?? ''}
              setSelectedProduct={handleSetSelectedProduct}
              setSelectedType={setSelectedType}
            />
            {/* <InstantSearch searchClient={searchClient}>
              <Index indexName={index}>
                <Configure hitsPerPage={30} />


            </Index>

              <Index indexName={index2}>
                <Configure hitsPerPage={30} />

              <ProductHits
                searchTerm={searchTerm ?? ''}
                setSelectedProduct={handleSetSelectedProduct}
                  setSelectedType={setSelectedType}
                />
              </Index>
            </InstantSearch> */}
          </div>
        )}

        {selectedType == 1 && selectedProduct && (
          <div className="mt-16 flex flex-col items-start gap-x-8 gap-y-16 sm:flex-row">
            <Image
              src={selectedProduct.thumbnail}
              alt={selectedProduct.title}
              height={200}
              width={200}
              className="aspect-square h-64 w-full rounded-2xl object-cover sm:w-64"
            />

            <div className="flex flex-col gap-y-6">
              <h1 className="w-full text-[2rem] font-semibold text-black sm:w-[60rem]">
                {selectedProduct.title}
              </h1>

              <div className="mt-4">
                <h3 className="text-[1.6rem] font-medium text-black">
                  Overview
                </h3>

                <div className="mt-4 flex items-center">
                  <div className="flex w-[12rem] flex-col gap-y-4">Sports:</div>

                  <div className="flex items-center gap-x-2">
                    {selectedProduct?.ride_types?.map((type, _) => (
                      <div
                        className="flex items-center  rounded-lg bg-[#EDEDED] px-2 py-px text-black"
                        key={_}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                </div>

                {(selectedProduct.size?.letter ||
                  selectedProduct.size?.number) && (
                  <div className="mt-4 flex items-center">
                    <div className="flex w-[12rem] flex-col gap-y-4">Size:</div>

                    <div className="flex items-center gap-x-2">
                      <div className="flex items-center  rounded-lg bg-[#EDEDED] px-2 py-px text-black">
                        {selectedProduct.size?.letter1 ||
                        selectedProduct.size?.letter ||
                        selectedProduct.size?.number ? (
                          <>
                            {selectedProduct.size?.letter1 || ''}
                            {selectedProduct.size?.letter || ''}
                            {selectedProduct.size?.number || ''}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedProduct.gender &&
                  selectedProduct.category !== 'Bike Parts' && (
                    <div className="mt-4 flex items-center">
                      <div className="flex w-[12rem] flex-col gap-y-4">
                        Gender:
                      </div>

                      <div className="flex items-center rounded-lg bg-[#EDEDED] px-2 py-px text-black">
                        {selectedProduct.gender}
                      </div>
                    </div>
                  )}

                <div className="mt-4 flex items-center">
                  <div className="flex w-[12rem] flex-col gap-y-4">
                    Category:
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {selectedProduct.categories?.map((cat, _) => (
                      <div
                        className="flex items-center rounded-lg bg-[#EDEDED] px-2 py-px text-black"
                        key={_}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center">
                  <div className="flex w-[12rem] flex-col gap-y-4">Brand:</div>

                  <div className="flex items-center rounded-lg bg-[#EDEDED] px-2 py-px text-black">
                    {selectedProduct.brand}
                  </div>
                </div>
                {!selectedProduct.has_variations && (
                  <div className="mt-4 flex items-center">
                    <div className="flex w-[12rem] flex-col gap-y-4">
                      Part Number:
                    </div>

                    <div className="flex items-center rounded-lg bg-[#EDEDED] px-2 py-px text-black">
                      {!!selectedProduct.mpn?.length
                        ? selectedProduct.mpn?.[0]
                        : selectedProduct.sku}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              className="mx-auto flex h-16 w-min cursor-pointer items-center justify-center  rounded-[1.5rem] bg-brand-primary p-2 px-4 text-white sm:h-12 sm:bg-brand-secondary sm:px-0"
              onClick={() => {
                setSelectedProduct(null);
              }}
            >
              <XMarkIcon className="h-[4.4rem] w-[4.4rem]" />

              <span className="flex w-full whitespace-nowrap pr-2 text-[1.8rem] sm:hidden">
                Deselect product
              </span>
            </button>
          </div>
        )}
        {selectedType === 3 && (
          <div className="my-8 flex flex-col gap-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <input
                type="text"
                className="w-full rounded-lg border border-zinc-300 px-4 py-2 text-[1.6rem] focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                placeholder="Enter OEM part number..."
                value={partNumber}
                maxLength={100}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setPartNumber(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handlePartNumberSearch();
                }}
                disabled={partLoading}
              />
              <button
                className="mt-2 rounded-lg bg-brand-secondary px-6 py-2 text-[1.6rem] font-semibold text-white hover:bg-brand-secondary-darker disabled:opacity-60 sm:ml-4 sm:mt-0"
                onClick={handlePartNumberSearch}
                disabled={partLoading || !partNumber.trim()}
                type="button"
              >
                {partLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            {partError && (
              <div className="mt-4 text-[1.5rem] text-red-600">{partError}</div>
            )}
            {partResult && (
              <div className="flex gap-8 rounded-lg border border-zinc-200 bg-white p-8">
                {/* {dataDepotImage && (
                  <SafeImage
                    src={dataDepotImage}
                    alt={partResult.title}
                    height={200}
                    width={200}
                    className="h-64 w-64 rounded-lg object-cover"
                  />
                )} */}
                <div>
                  <div className="mb-2 text-[1.7rem] font-bold">
                    {partResult.title}
                  </div>
                  <div className="mb-2 text-[1.7rem] font-bold">
                    Suggested Retail Price:{' '}
                    {formatCurrency(partResult.price ?? 0)}
                  </div>
                  <div className="mb-4 text-[1.5rem]">
                    {partResult.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                      ? partResult.description
                      : ''}
                  </div>
                </div>
              </div>
            )}
            {fitmentData && (
              <div className="mt-6">
                <div className="rounded-lg border border-zinc-200 bg-white p-4">
                  <div className="mb-2 font-bold">Fitment Data</div>
                  <table className="w-full text-[1.4rem]">
                    <thead>
                      <tr>
                        <th className="border-b-2 border-zinc-200 p-2 text-left">
                          Years
                        </th>
                        <th className="border-b-2 border-zinc-200 p-2 text-left">
                          Make
                        </th>
                        <th className="border-b-2 border-zinc-200 p-2 text-left">
                          Model
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fitmentData.bikes.length > 0 ? (
                        Object.entries(
                          fitmentData.bikes.reduce((acc, bike) => {
                            const key = `${bike.make} ${bike.model}`;
                            if (!acc[key]) {
                              acc[key] = {
                                make: bike.make,
                                model: bike.model,
                                years: [],
                              };
                            }
                            acc[key].years.push(bike.year);
                            return acc;
                          }, {} as Record<string, { make: string; model: string; years: number[] }>)
                        ).map(([key, { make, model, years }]) => {
                          const minYear = Math.min(...years);
                          const maxYear = Math.max(...years);
                          const isSame = minYear === maxYear;
                          return (
                            <tr key={key}>
                              <td className="border-b border-zinc-200 p-2">
                                {isSame ? minYear : `${minYear}-${maxYear}`}
                              </td>
                              <td className="border-b border-zinc-200 p-2">
                                {make}
                              </td>
                              <td className="border-b border-zinc-200 p-2">
                                {model}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center">
                            No fitment data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex w-full justify-center sm:mb-[10rem]">
        <button
          className="flex  items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-secondary px-8 py-4 text-[1.8rem] text-white hover:bg-brand-secondary-darker sm:grow-0"
          type="button"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

const ProductHits = ({
  searchTerm,
  hits,
  setSelectedProduct,
  setSelectedType,
  isLoading,
}: {
  searchTerm: string;
  hits?: SearchResponseHit<ProductDocument>[];
  setSelectedProduct: (product: ProductDocument) => void;
  setSelectedType: (step: number) => void;
  isLoading: boolean;
}) => {
  if (searchTerm && (!hits || isLoading))
    return (
      <div className="flex h-[30rem] w-full items-center justify-center">
        <div className="flex flex-col items-center gap-y-6">
          <h1 className="text-[2.4rem] text-zinc-700">Loading...</h1>
        </div>
      </div>
    );

  if (hits && hits.length === 0 && searchTerm) {
    return (
      <div className="flex h-[30rem] w-full items-center justify-center">
        <div className="flex flex-col items-center gap-y-6">
          <h1 className="text-[2.4rem] text-zinc-700">No items found.</h1>

          <button
            className="flex items-center justify-center gap-x-2 rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] font-semibold text-white"
            type="button"
            onClick={() => {
              setSelectedType(2);
            }}
          >
            <PaperIcon variant="white" className="h-8 w-8" />
            Start from scratch
          </button>
        </div>
      </div>
    );
  }

  if (hits) {
    return (
      <div className="mt-4 h-[40rem] overflow-y-auto">
        <ul className="space-y-2">
          {hits.map((hit, index) => (
            <li
              key={hit.document.id}
              className="cursor-pointer rounded-[1.5rem] bg-[#F6F6F6] p-6 hover:bg-[#F2F2F2]"
              onClick={() => {
                setSelectedProduct(hit.document);
              }}
            >
              <div className="flex flex-col gap-x-4 sm:flex-row sm:items-center">
                <div className="flex items-center gap-x-4">
                  <SafeImage
                    src={hit.document.thumbnail}
                    alt={hit.document.title}
                    height={200}
                    width={200}
                    className="aspect-square h-32 w-32 rounded-2xl object-contain"
                  />
                  <ProductVariationImages product={hit.document} />
                </div>

                <div className="flex flex-col gap-y-2">
                  {/* <h1 className="text-[1.8rem] font-semibold text-black"> */}
                  {/* {hit.document.title} */}
                  {/* </h1> */}
                  {hit.highlight.title?.snippet ? (
                    <div
                      className="highlighted-search text-[1.8rem]"
                      dangerouslySetInnerHTML={{
                        __html: hit.highlight.title.snippet,
                      }}
                      key={`${hit.document.id}-${index}`}
                    ></div>
                  ) : (
                    <div
                      className="text-[1.8rem]"
                      key={`${hit.document.id}-${index}`}
                    >
                      {hit.document.title}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {[
                      ...(hit.document.ride_types ?? []),
                      ...hit.document.categories,
                    ].map((cat) => (
                      <div
                        className="flex items-center justify-center rounded-lg border border-zinc-200 px-2 py-px"
                        key={cat}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                  {/* part number */}
                  {!hit.document.has_variations && (
                    <>
                      {hit.highlight.sku?.snippet ? (
                        <div
                          className="highlighted-search text-[1.8rem]"
                          dangerouslySetInnerHTML={{
                            __html: hit.highlight.sku.snippet,
                          }}
                          key={`${hit.document.id}-${index}`}
                        ></div>
                      ) : hit.document.mpn?.length ? (
                        <div
                          className="text-[1.8rem]"
                          key={`${hit.document.mpn?.[0]}-${index}`}
                        >
                          {hit.document.mpn?.[0]}
                        </div>
                      ) : (
                        <div
                          className="text-[1.8rem]"
                          key={`${hit.document.sku}-${index}`}
                        >
                          {hit.document.sku}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
};

const ListingTypeComponent = ({
  selectedType,
  type,
  icon,
  title,
  isBeta,
  description,
  setSelectedType,
  children,
}: {
  selectedType: number | null;
  type: number;
  icon: JSX.Element;
  title: string;
  isBeta?: boolean;
  description: string;
  setSelectedType: () => void;
  children?: React.ReactNode;
}) => {
  return (
    <div
      className={`rounded-2xl border ${
        type === selectedType
          ? 'border-brand-secondary'
          : 'border-brand-lightest-gray'
      } w-full cursor-pointer p-16 hover:border-brand-secondary`}
      onClick={setSelectedType}
    >
      <div className="flex items-center gap-x-8">
        {icon}

        <div className="flex flex-col gap-y-2">
          <div className="flex items-center gap-x-4">
            <h3 className="text-[1.8rem] font-semibold text-black">{title}</h3>

            {isBeta && (
              <div className="flex items-center justify-center rounded-2xl bg-brand-secondary px-4 py-2 text-[1.3rem] font-semibold text-white">
                BETA
              </div>
            )}
          </div>

          <p className="text-[1.5rem] text-zinc-700">{description}</p>
          {children}
        </div>
      </div>
    </div>
  );
};

const StarsIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_11015_98023)">
        <path
          opacity="0.6"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M25.95 21.691C25.815 21.279 25.435 21 25 21C24.565 21 24.185 21.279 24.05 21.691L23.1 24.6025L20.19 25.549C19.78 25.683 19.5 26.067 19.5 26.5C19.5 26.933 19.78 27.317 20.19 27.451L23.1 28.3975L24.05 31.309C24.185 31.721 24.565 32 25 32C25.435 32 25.815 31.721 25.95 31.309L26.9 28.3975L29.81 27.451C30.22 27.317 30.5 26.933 30.5 26.5C30.5 26.067 30.22 25.683 29.81 25.549L26.9 24.6025L25.95 21.691Z"
          fill="#C41719"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 18.4995C0 19.23 0.469978 19.8795 1.16498 20.1055L8.51001 22.4925L10.895 29.834C11.12 30.529 11.77 31 12.5 31C13.23 31 13.88 30.5295 14.105 29.834L16.495 22.4925L23.835 20.1055C24.53 19.8795 25 19.2305 25 18.5C25 17.769 24.53 17.1205 23.835 16.8945L16.495 14.5075L14.105 7.166C13.88 6.4705 13.23 6 12.5 6C11.77 6 11.12 6.471 10.895 7.166L8.51001 14.5075L1.16498 16.8945C0.469978 17.1205 0 17.7685 0 18.4995Z"
          fill="#C41719"
        />
        <path
          opacity="0.4"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M25.45 0.691C25.315 0.279 24.935 0 24.5 0C24.065 0 23.685 0.279 23.55 0.691L22.11 5.1115L17.69 6.549C17.28 6.683 17 7.067 17 7.5C17 7.933 17.28 8.317 17.69 8.451L22.11 9.8885L23.55 14.309C23.685 14.721 24.065 15 24.5 15C24.935 15 25.315 14.721 25.45 14.309L26.89 9.8885L31.31 8.451C31.72 8.317 32 7.933 32 7.5C32 7.067 31.72 6.683 31.31 6.549L26.89 5.1115L25.45 0.691Z"
          fill="#C41719"
        />
        <path
          opacity="0.2"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4 6.5H3C2.45 6.5 2 6.948 2 7.5C2 8.052 2.45 8.5 3 8.5H4V9.5C4 10.052 4.45 10.5 5 10.5C5.55 10.5 6 10.052 6 9.5V8.5H7C7.55 8.5 8 8.052 8 7.5C8 6.948 7.55 6.5 7 6.5H6V5.5C6 4.948 5.55 4.5 5 4.5C4.45 4.5 4 4.948 4 5.5V6.5Z"
          fill="#C41719"
        />
      </g>
      <defs>
        <clipPath id="clip0_11015_98023">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const PaperIcon = ({
  className,
  variant,
}: {
  className?: string;
  variant: 'white' | 'red';
}) => {
  const fillColor = variant === 'white' ? '#FFFFFF' : '#C41719';

  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        opacity="0.2"
        d="M26.2747 9.7253L19.608 3.05864C19.3587 2.80797 19.0187 2.66797 18.6654 2.66797H7.9987C6.52803 2.66797 5.33203 3.86264 5.33203 5.33464V26.668C5.33203 28.14 6.52803 29.3346 7.9987 29.3346H23.9987C25.4694 29.3346 26.6654 28.14 26.6654 26.668V10.668C26.6654 10.3133 26.5254 9.97464 26.2747 9.7253Z"
        fill={fillColor}
      />
      <path
        d="M26.6654 10.668V12.0013H19.9987C18.532 12.0013 17.332 10.8013 17.332 9.33464V2.66797H18.6654C18.9987 2.66797 19.3054 2.78797 19.5454 3.0013L26.332 9.78797C26.5454 10.028 26.6654 10.3346 26.6654 10.668Z"
        fill={fillColor}
      />
    </svg>
  );
};
